import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Container,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { supabase } from '../services/supabaseClient';
import { SYSTEM_MODULES, MODULE_CATEGORY_MAP } from '../constants/modules';

interface Modulo {
    id: string;
    nome: string;
}

const ModuloManagementPage: React.FC = () => {
    const [modulos, setModulos] = useState<Modulo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

    const fetchModulos = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data, error } = await supabase.rpc('get_modulos');
            if (error) throw error;
            setModulos(data || []);
        } catch (err: any) {
            setError(err.message || 'Erro ao carregar módulos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchModulos();
    }, []);

    const handleSyncModules = async () => {
        setSyncLoading(true);
        setSyncSuccess(null);
        setError(null);
        try {
            const { error } = await supabase.rpc('sync_system_modules', { p_modules: SYSTEM_MODULES });
            if (error) throw error;
            setSyncSuccess(
                'Módulos sincronizados com sucesso! Todos os módulos do sistema estão cadastrados e vinculados ao Administrador.'
            );
            fetchModulos();
        } catch (err: any) {
            setError(err.message || 'Erro ao sincronizar módulos.');
        } finally {
            setSyncLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container component="main" maxWidth="md">
            <Box sx={{ my: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                            Gerenciamento de Módulos
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sincronize as funcionalidades do código-fonte com o controle de permissões do banco de dados
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={syncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                        onClick={handleSyncModules}
                        disabled={syncLoading}
                        sx={{ fontWeight: 600, px: 2.5, py: 1, borderRadius: 2, flexShrink: 0 }}
                    >
                        {syncLoading ? 'Sincronizando...' : 'Sincronizar Módulos'}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {syncSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSyncSuccess(null)}>
                        {syncSuccess}
                    </Alert>
                )}

                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Nome do Módulo</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Grupo / Área</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {modulos.map((modulo) => {
                                const isActive = SYSTEM_MODULES.includes(modulo.nome);
                                const grupo = MODULE_CATEGORY_MAP[modulo.nome] || 'Outros';
                                return (
                                    <TableRow key={modulo.id} hover>
                                        <TableCell sx={{ fontWeight: 500 }}>{modulo.nome}</TableCell>
                                        <TableCell>
                                            <Chip label={grupo} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            {isActive ? (
                                                <Chip
                                                    icon={<CheckCircleOutlineIcon />}
                                                    label="Ativo no Sistema"
                                                    color="success"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<WarningAmberIcon />}
                                                    label="Obsoleto (Não encontrado no código)"
                                                    color="warning"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Container>
    );
};

export default ModuloManagementPage;
